﻿import { hasDataPage, qs } from "../core/dom";
import {
    normalizeHex,
    hexToRgb,
    rgbToHsl,
    generateShadeScale,
    generatePalette,
    buildGradientCss,
    contrastRatio,
    contrastRating,
    buildShadowCss,
    buildCssVariables,
    buildScssVariables,
    buildJsonScale,
    buildTailwindLikeScale,
    buildUtilityClasses,
    buildSemanticTokens,
    randomHexColor,
    buildOpacityScale,
    bestContrastText,
    buildTailwindConfig,
    parseColorInput,
    buildThemePack,
    generateShadeScaleHsl,
    generateShadeScaleOklch,
} from "../lib/color-utils";

export function initColorToolsPage() {
    if (!hasDataPage("color-tools")) return;

    const page = {
        baseColorPicker: qs("[data-base-color-picker]"),
        baseColorInput: qs("[data-base-color-input]"),
        colorHex: qs("[data-color-hex]"),
        colorRgb: qs("[data-color-rgb]"),
        colorHsl: qs("[data-color-hsl]"),

        tokenPrefix: qs("[data-token-prefix]"),
        shadeScale: qs("[data-shade-scale]"),
        exportFormat: qs("[data-export-format]"),

        paletteType: qs("[data-palette-type]"),
        paletteCount: qs("[data-palette-count]"),

        gradientType: qs("[data-gradient-type]"),
        gradientAngle: qs("[data-gradient-angle]"),
        gradientStart: qs("[data-gradient-start]"),
        gradientEnd: qs("[data-gradient-end]"),

        contrastFg: qs("[data-contrast-fg]"),
        contrastBg: qs("[data-contrast-bg]"),

        shadowX: qs("[data-shadow-x]"),
        shadowY: qs("[data-shadow-y]"),
        shadowBlur: qs("[data-shadow-blur]"),
        shadowSpread: qs("[data-shadow-spread]"),
        shadowOpacity: qs("[data-shadow-opacity]"),

        shadeGrid: qs("[data-shade-grid]"),
        paletteGrid: qs("[data-palette-grid]"),
        gradientPreview: qs("[data-gradient-preview]"),
        gradientCode: qs("[data-gradient-code]"),
        contrastPreview: qs("[data-contrast-preview]"),
        contrastMeta: qs("[data-contrast-meta]"),
        shadowPreviewBox: qs("[data-shadow-preview-box]"),
        shadowCode: qs("[data-shadow-code]"),

        exportCss: qs("[data-export-css]"),
        exportScss: qs("[data-export-scss]"),
        exportJson: qs("[data-export-json]"),

        viewTabs: Array.from(document.querySelectorAll("[data-color-view-tab]")),
        views: Array.from(document.querySelectorAll("[data-color-view]")),

        copyExportBtn: qs("[data-copy-export]"),
        downloadExportBtn: qs("[data-download-export]"),
        loadExampleBtn: qs("[data-load-example]"),
        resetBtn: qs("[data-reset-color-tool]"),
        semanticName: qs("[data-semantic-name]"),
        randomPaletteBtn: qs("[data-random-palette]"),
        opacityGrid: qs("[data-opacity-grid]"),
        shufflePaletteBtn: qs("[data-shuffle-palette]"),
        shadowPresetButtons: Array.from(document.querySelectorAll("[data-shadow-preset]")),

        themePackName: qs("[data-theme-pack-name]"),
        generateThemePackBtn: qs("[data-generate-theme-pack]"),
        exportThemePack: qs("[data-export-theme-pack]"),
        shadeMode: qs("[data-shade-mode]"),
    };

    bindEvents(page);
    renderColorTool(page);
}

function bindEvents(page) {
    [
        //page.baseColorPicker,
        //page.baseColorInput,
        page.tokenPrefix,
        page.shadeScale,
        page.exportFormat,
        page.paletteType,
        page.paletteCount,
        page.gradientType,
        page.gradientAngle,
        page.gradientStart,
        page.gradientEnd,
        page.contrastFg,
        page.contrastBg,
        page.shadowX,
        page.shadowY,
        page.shadowBlur,
        page.shadowSpread,
        page.shadowOpacity,
        page.semanticName,
        page.shadeMode,
    ].forEach(el => {
        if (!el) return;
        const eventName = el.type === "color" || el.tagName === "SELECT" ? "input" : "input";
        el.addEventListener(eventName, () => renderColorTool(page));
    });

    page.baseColorPicker?.addEventListener("input", () => {
        const color = page.baseColorPicker.value.toUpperCase();
        page.baseColorInput.value = color;
        renderColorTool(page, { resolvedBaseHex: color, preserveInput: true });
    });

    page.baseColorInput?.addEventListener("input", () => {
        const raw = page.baseColorInput.value.trim();
        const parsed = parseColorInput(raw);

        // geçerli bir renkse picker'ı güncelle ve render et
        if (parsed) {
            page.baseColorPicker.value = parsed;
            renderColorTool(page, { resolvedBaseHex: parsed, preserveInput: true });
            return;
        }

        // geçersiz / incomplete input sırasında sadece text kalsın, render fallback ezmesin
    });

    page.baseColorInput?.addEventListener("blur", () => {
        const parsed = parseColorInput(page.baseColorInput.value.trim());

        if (parsed) {
            page.baseColorInput.value = parsed;
            page.baseColorPicker.value = parsed;
            renderColorTool(page, { resolvedBaseHex: parsed, preserveInput: true });
        }
    });

    page.themePackName?.addEventListener("input", () => renderColorTool(page));

    page.generateThemePackBtn?.addEventListener("click", () => {
        activateColorView(page, "theme-pack");
        renderColorTool(page);
    });

    page.randomPaletteBtn?.addEventListener("click", () => {
        const color = randomHexColor();
        page.baseColorInput.value = color;
        page.baseColorPicker.value = color;
        renderColorTool(page);
    });

    page.viewTabs.forEach(tab => {
        tab.addEventListener("click", () => {
            const target = tab.dataset.colorViewTab;

            page.viewTabs.forEach(x => x.classList.toggle("active", x === tab));
            page.views.forEach(view => {
                const isActive = view.dataset.colorView === target;
                view.hidden = !isActive;
                view.classList.toggle("active", isActive);
            });
        });
    });

    page.shadowPresetButtons.forEach(btn => {
        btn.addEventListener("click", () => {
            const preset = btn.dataset.shadowPreset;

            if (preset === "soft") {
                page.shadowX.value = "0";
                page.shadowY.value = "8";
                page.shadowBlur.value = "20";
                page.shadowSpread.value = "0";
                page.shadowOpacity.value = "0.12";
            }

            if (preset === "medium") {
                page.shadowX.value = "0";
                page.shadowY.value = "12";
                page.shadowBlur.value = "30";
                page.shadowSpread.value = "0";
                page.shadowOpacity.value = "0.18";
            }

            if (preset === "deep") {
                page.shadowX.value = "0";
                page.shadowY.value = "20";
                page.shadowBlur.value = "50";
                page.shadowSpread.value = "-4";
                page.shadowOpacity.value = "0.28";
            }

            renderColorTool(page);
        });
    });

    page.copyExportBtn?.addEventListener("click", async () => {
        const content = getActiveExport(page);
        await navigator.clipboard.writeText(content);
    });

    page.shufflePaletteBtn?.addEventListener("click", () => {
        const color = randomHexColor();
        page.baseColorInput.value = color;
        page.baseColorPicker.value = color;
        renderColorTool(page);
    });

    page.downloadExportBtn?.addEventListener("click", () => {
        const active = page.exportFormat.value;
        const content = getActiveExport(page);

        let ext = "css";
        if (active === "scss") ext = "scss";
        if (active === "json" || active === "tailwind") ext = "json";
        if (active === "utility") ext = "css";
        if (active === "semantic") ext = "css";

        downloadText(`color-export.${ext}`, content);
    });

    page.loadExampleBtn?.addEventListener("click", () => {
        page.baseColorInput.value = "#6366F1";
        page.baseColorPicker.value = "#6366F1";
        page.tokenPrefix.value = "mix-color";
        page.paletteType.value = "analogous";
        page.paletteCount.value = "5";
        page.gradientType.value = "linear";
        page.gradientAngle.value = "135";
        page.gradientStart.value = "#6366F1";
        page.gradientEnd.value = "#8B5CF6";
        page.contrastFg.value = "#FFFFFF";
        page.contrastBg.value = "#111827";
        page.shadowX.value = "0";
        page.shadowY.value = "12";
        page.shadowBlur.value = "30";
        page.shadowSpread.value = "0";
        page.shadowOpacity.value = "0.18";
        renderColorTool(page);
    });

    page.resetBtn?.addEventListener("click", () => {
        page.baseColorInput.value = "#6366F1";
        page.baseColorPicker.value = "#6366F1";
        page.tokenPrefix.value = "mix-color";
        page.shadeScale.value = "tailwind";
        page.exportFormat.value = "css";
        page.paletteType.value = "analogous";
        page.paletteCount.value = "5";
        page.gradientType.value = "linear";
        page.gradientAngle.value = "135";
        page.gradientStart.value = "#6366F1";
        page.gradientEnd.value = "#8B5CF6";
        page.contrastFg.value = "#FFFFFF";
        page.contrastBg.value = "#111827";
        page.shadowX.value = "0";
        page.shadowY.value = "12";
        page.shadowBlur.value = "30";
        page.shadowSpread.value = "0";
        page.shadowOpacity.value = "0.18";
        renderColorTool(page);
    });
}

function renderColorTool(page, options = {}) {
    const {
        resolvedBaseHex = null,
        preserveInput = false
    } = options;

    const parsedFromInput = parseColorInput(page.baseColorInput.value);
    const baseHex = resolvedBaseHex || parsedFromInput || "#6366F1";

    // sadece istemiyorsak input'u normalize etme
    if (!preserveInput) {
        page.baseColorInput.value = baseHex;
    }

    page.baseColorPicker.value = baseHex;

    const rgb = hexToRgb(baseHex);
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

    page.colorHex.value = baseHex;
    page.colorRgb.value = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
    page.colorHsl.value = `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;

    const shadeMode = page.shadeMode?.value || "mix";

    const shades =
        shadeMode === "hsl"
            ? generateShadeScaleHsl(baseHex, page.shadeScale.value)
            : shadeMode === "oklch"
                ? generateShadeScaleOklch(baseHex, page.shadeScale.value)
                : generateShadeScale(baseHex, page.shadeScale.value);

    const palette = generatePalette(baseHex, page.paletteType.value, page.paletteCount.value);

    renderShadeGrid(page, shades);
    renderPaletteGrid(page, palette);
    renderGradient(page);
    renderContrast(page);
    renderShadow(page);
    renderOpacityScale(page, baseHex);
    renderExports(page, shades);
    syncVisibleExport(page);
}

function renderOpacityScale(page, baseHex) {
    const items = buildOpacityScale(baseHex);

    page.opacityGrid.innerHTML = items.map(item => `
        <div class="opacity-card">
            <div class="opacity-card__swatch" style="background:${item.value}"></div>
            <strong>${item.step}%</strong>
            <span>${item.value}</span>
        </div>
    `).join("");
}

function syncVisibleExport(page) {
    const activeFormat = page.exportFormat.value;
    const content = getActiveExport(page);

    if (activeFormat === "scss") {
        page.exportScss.textContent = content;
        return;
    }

    if (activeFormat === "json" || activeFormat === "tailwind") {
        page.exportJson.textContent = content;
        return;
    }

    page.exportCss.textContent = content;
}

function activateColorView(page, target) {
    page.viewTabs.forEach(tab => {
        tab.classList.toggle("active", tab.dataset.colorViewTab === target);
    });

    page.views.forEach(view => {
        const isActive = view.dataset.colorView === target;
        view.hidden = !isActive;
        view.classList.toggle("active", isActive);
    });
}

function renderShadeGrid(page, shades) {
    page.shadeGrid.innerHTML = shades.map(item => {
        const textColor = bestContrastText(item.color);

        return `
            <div class="shade-card">
                <div class="shade-card__swatch" style="background:${item.color}; color:${textColor}">
                    <button type="button"
                            class="shade-card__copy"
                            data-copy-color="${item.color}"
                            aria-label="Copy ${item.color}">
                        <i class="fa-regular fa-copy"></i>
                    </button>

                    <div class="shade-card__overlay-label">${item.step}</div>
                </div>
                <div class="shade-card__meta">
                    <strong>${item.step}</strong>
                    <span>${item.color}</span>
                </div>
            </div>
        `;
    }).join("");

    page.shadeGrid.querySelectorAll("[data-copy-color]").forEach(btn => {
        btn.addEventListener("click", async () => {
            const value = btn.dataset.copyColor || "";
            await navigator.clipboard.writeText(value);

            btn.classList.add("is-copied");
            setTimeout(() => btn.classList.remove("is-copied"), 900);
        });
    });
}

function renderPaletteGrid(page, palette) {
    page.paletteGrid.innerHTML = palette.map(color => {
        const textColor = bestContrastText(color);

        return `
            <div class="palette-card">
                <div class="palette-card__swatch" style="background:${color}; color:${textColor}">
                    <button type="button"
                            class="shade-card__copy"
                            data-copy-color="${color}"
                            aria-label="Copy ${color}">
                        <i class="fa-regular fa-copy"></i>
                    </button>

                    <div class="shade-card__overlay-label">${color}</div>
                </div>
                <span>${color}</span>
            </div>
        `;
    }).join("");

    page.paletteGrid.querySelectorAll("[data-copy-color]").forEach(btn => {
        btn.addEventListener("click", async () => {
            const value = btn.dataset.copyColor || "";
            await navigator.clipboard.writeText(value);

            btn.classList.add("is-copied");
            setTimeout(() => btn.classList.remove("is-copied"), 900);
        });
    });
}

function renderGradient(page) {
    const css = buildGradientCss(
        page.gradientType.value,
        Number(page.gradientAngle.value || 135),
        page.gradientStart.value,
        page.gradientEnd.value
    );

    page.gradientPreview.style = css;
    page.gradientCode.textContent = css;
}

function renderContrast(page) {
    const fg = page.contrastFg.value;
    const bg = page.contrastBg.value;
    const ratio = contrastRatio(fg, bg);
    const rating = contrastRating(ratio);

    page.contrastPreview.style.background = bg;
    page.contrastPreview.style.color = fg;

    const heading = page.contrastPreview.querySelector("h4");
    const text = page.contrastPreview.querySelector("p");

    if (heading) {
        heading.style.color = fg;
    }

    if (text) {
        text.style.color = fg;
    }

    page.contrastMeta.innerHTML = `
        <div class="contrast-stat">
            <strong>Ratio</strong>
            <span>${ratio}:1</span>
        </div>
        <div class="contrast-stat">
            <strong>Rating</strong>
            <span>${rating}</span>
        </div>
    `;
}

function renderShadow(page) {
    const css = buildShadowCss(
        Number(page.shadowX.value || 0),
        Number(page.shadowY.value || 0),
        Number(page.shadowBlur.value || 30),
        Number(page.shadowSpread.value || 0),
        page.baseColorInput.value,
        Number(page.shadowOpacity.value || 0.18)
    );

    page.shadowPreviewBox.style.boxShadow = css.replace("box-shadow: ", "").replace(";", "");
    page.shadowCode.textContent = css;
}

function renderExports(page, shades) {
    const prefix = (page.tokenPrefix.value || "mix-color").trim();
    const semanticName = (page.semanticName.value || "primary").trim();

    page.exportCss.textContent = buildCssVariables(prefix, shades);
    page.exportScss.textContent = buildScssVariables(prefix, shades);
    page.exportJson.textContent = buildJsonScale(shades);

    //page.exportCss.dataset.altTailwind = buildTailwindLikeScale(shades);
    page.exportCss.dataset.altUtility = buildUtilityClasses(prefix, shades);
    page.exportCss.dataset.altSemantic = buildSemanticTokens(semanticName, shades);
    page.exportCss.dataset.altTailwind = buildTailwindConfig(prefix, shades);

    const baseHex = parseColorInput(page.baseColorInput.value) || "#6366F1";
    const themeName = (page.themePackName.value || "brand").trim();

    page.exportThemePack.textContent = buildThemePack(baseHex, themeName);
}

function getActiveExportView(page) {
    const active = page.views.find(x => x.classList.contains("active"));
    return active?.dataset.colorView || "css";
}

function getActiveExport(page) {
    const active = page.exportFormat.value;

    if (active === "scss") return page.exportScss.textContent || "";
    if (active === "json") return page.exportJson.textContent || "";
    if (active === "tailwind") return page.exportCss.dataset.altTailwind || "";
    if (active === "utility") return page.exportCss.dataset.altUtility || "";
    if (active === "semantic") return page.exportCss.dataset.altSemantic || "";
    if (active === "theme-pack") return page.exportThemePack.textContent || "";

    return page.exportCss.textContent || "";
}

function downloadText(fileName, content) {
    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();

    setTimeout(() => URL.revokeObjectURL(url), 500);
}